<script setup lang="ts">
import OpenNewWindowIcon from "@/components/AuditLog/OpenNewWindowIcon.vue";
import selectRoleChecker from "@/router/selectRoleChecker";
import { formatDateTime } from "@/services/functions";
import Api from "@/services/api/ApiServiceFabrick";
import auditLog from "@/types/responses/auditLog";
import SelfAccount from "@/types/SelfAccount";
import { onMounted, ref } from "vue";
import i18n from "@/lang/i18n";
import {
  SELECTABLE_AUDITABLE_TYPE_ITEMS,
  SELECTABLE_EVENT_ITEMS,
  ID_LABELS,
  OPERATION_OFFICE_TYPE,
  DEPARTMENT_OFFICE_TYPE,
} from '@/services/consts';

const props = defineProps<{
  auditLogs: auditLog[]
  isLoading: boolean
  isSearchCompleted: boolean
}>()

const SELECTED_ROLE = selectRoleChecker.getCurrentRole()
const IS_OPERATION_OR_DEPARTMENT = SELECTED_ROLE && (SELECTED_ROLE.organization_type === OPERATION_OFFICE_TYPE || SELECTED_ROLE.organization_type === DEPARTMENT_OFFICE_TYPE)
const selfAccount = ref<SelfAccount | null>(null)

onMounted(async () => {
  if (IS_OPERATION_OR_DEPARTMENT) {
    await Api().getSelfAccount().then((data: SelfAccount) => {
      selfAccount.value = data
    })
  }
})

function displayType(auditLog: auditLog) {
  return SELECTABLE_AUDITABLE_TYPE_ITEMS.find(item => item.value === auditLog.auditable_type)?.name
}

function displayEventName(auditLog: auditLog) {
  return SELECTABLE_EVENT_ITEMS.find(item => item.value === auditLog.event)?.name
}

function eventNameColor(auditLog: auditLog) {
  switch (auditLog.event) {
    case 'created':
      return 'rgb(21, 130, 156)'
    case 'deleted':
      return 'danger'
    default:
      return ''
  }
}

function displayIdLabel(auditLog: auditLog) {
  const idLabel = ID_LABELS[auditLog.auditable_type as keyof typeof ID_LABELS] ?? i18n.global.t('pages.auditLog.list.id')
  const auditableId = auditLog.auditable_type == 'workload' ? auditLog.auditable.delivery_plan_id : auditLog.auditable_id
  return `${idLabel}: ${auditableId}`
}

function isAvailableOperationOffice(auditLog: auditLog) {
  if (!IS_OPERATION_OR_DEPARTMENT || auditLog.auditable_type !== 'operation_office') {
    return true
  }

  return selfAccount.value?.officeable_id == auditLog.auditable_id
}

function isDisplayOpenNewWindowIcon(auditLog: auditLog) {
  const isDeleted = auditLog.event === 'deleted';

  return !isDeleted && isAvailableOperationOffice(auditLog);
}
</script>
<template>
  <tbody>
    <template v-if="props.auditLogs.length">
      <tr v-for="auditLog in props.auditLogs" :key="auditLog.id">
        <td class="column-space id-column">
          <div class="ml-2">
            {{ auditLog.id }}
          </div>
        </td>
        <td class="column-space">
          {{ displayType(auditLog) }}
        </td>
        <td class="column-space">
          <v-chip
            variant="tonal"
            :color="eventNameColor(auditLog)"
          >
            {{ displayEventName(auditLog) }}
          </v-chip>
        </td>
        <td class="column-space">
          <div class="d-flex w-75">
            <div class="w-75 d-flex align-items-center">
              {{ displayIdLabel(auditLog) }}
            </div>
            <div class="w-25 justify-content-between align-items-center">
              <OpenNewWindowIcon
                v-if="isDisplayOpenNewWindowIcon(auditLog)"
                :auditLog="auditLog"
              />
            </div>
          </div>
        </td>
        <td class="column-space">
          {{ auditLog.user.id }}
        </td>
        <td class="column-space">
          {{ auditLog.user.name }}
        </td>
        <td class="column-space">
          {{ formatDateTime(new Date(auditLog.created_at)) }}
        </td>
        <td class="column-space">
          {{ auditLog.ip_address }}
        </td>
      </tr>
    </template>
    <template
      v-else-if="!props.auditLogs?.length && !props.isLoading && props.isSearchCompleted"
    >
      <tr>
        <td colspan="9">
          {{ i18n.global.t('pages.common.list.noData') }}
        </td>
      </tr>
    </template>
  </tbody>
</template>
